import React, { useState, useEffect, useCallback } from "react";
import { Sparkles, Check, X, RefreshCw, ChevronDown, Loader2 } from "lucide-react";

const TABS = [
  { key: "pending_review", label: "Pending" },
  { key: "needs_review", label: "Needs review" },
  { key: "added_to_db", label: "Added" },
  { key: "rejected", label: "Rejected" },
];

const CATEGORY_HINTS = ["dairy", "grain", "grains_bakery", "protein", "meat", "fish", "vegetable", "fruit", "beverages", "eggs", "nuts", "sweets", "oils", "other"];
const GREEN = "#7B9E7F";
const TEXT = "#3D4035";

function draftToForm(d = {}) {
  return {
    placement: d.placement === "subtype" ? "subtype" : "new",
    parent_key: d.parent_key || "",
    category: d.category || "",
    icon: d.icon || "",
    shelf_life: d.shelf_life ?? "",
    ua: d.ua || "",
    en: d.en || "",
    ru: d.ru || "",
    cal: d.cal ?? "",
    p: d.p ?? "",
    f: d.f ?? "",
    c: d.c ?? "",
    vitamins: Array.isArray(d.vitamins) ? d.vitamins.join(", ") : (d.vitamins || ""),
    product_key: d.ua || "",
  };
}

function formToPayload(form) {
  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
  const product = {
    category: form.category || null,
    ua: form.ua,
    en: form.en,
    ru: form.ru,
    cal: num(form.cal),
    p: num(form.p),
    f: num(form.f),
    c: num(form.c),
    vitamins: form.vitamins ? form.vitamins.split(",").map((s) => s.trim()).filter(Boolean) : [],
  };
  if (form.placement === "new") {
    product.icon = form.icon || null;
    product.shelf_life = num(form.shelf_life);
  }
  return {
    product,
    placement: form.placement,
    parent_key: form.placement === "subtype" ? form.parent_key : null,
    product_key: form.placement === "new" ? (form.product_key || form.ua) : null,
  };
}

export default function UnknownProductsQueue({ api }) {
  const [tab, setTab] = useState("pending_review");
  const [lists, setLists] = useState({});
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState({});

  const products = lists[tab] || [];

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const res = await api().get(`/admin/unknown-products?status=${status}`);
      setLists((prev) => ({ ...prev, [status]: res.data.products || [] }));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { setExpanded(null); load(tab); }, [tab, load]);

  const setB = (name, v) => setBusy((p) => ({ ...p, [name]: v }));
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openForm = (name, draft) => { setExpanded(name); setForm(draftToForm(draft)); };

  const enrich = async (name, suggestedCat) => {
    setB(name, true);
    try {
      const res = await api().post(`/admin/unknown-products/${encodeURIComponent(name)}/enrich`);
      const draft = res.data.draft || {};
      if (!draft.category && suggestedCat) draft.category = suggestedCat;
      openForm(name, draft);
    } catch (e) {
      alert("Enrich failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setB(name, false);
    }
  };

  const approve = async (name) => {
    setB(name, true);
    try {
      await api().post(`/admin/unknown-products/${encodeURIComponent(name)}/approve`, formToPayload(form));
      setExpanded(null);
      await load(tab);
    } catch (e) {
      alert("Approve failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setB(name, false);
    }
  };

  const reject = async (name) => {
    setB(name, true);
    try {
      await api().post(`/admin/unknown-products/${encodeURIComponent(name)}/reject`);
      setExpanded(null);
      await load(tab);
    } catch (e) {
      alert("Reject failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setB(name, false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-semibold" style={{ color: TEXT }}>Unknown products</h2>
        <button onClick={() => load(tab)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <RefreshCw className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {TABS.map((tb) => {
          const active = tb.key === tab;
          const n = lists[tb.key]?.length;
          return (
            <button key={tb.key} onClick={() => setTab(tb.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${active ? "text-white" : "text-gray-500 border border-gray-200 hover:bg-gray-100"}`}
              style={active ? { background: GREEN } : {}}>
              {tb.label}{n != null ? ` (${n})` : ""}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center pt-10"><Loader2 className="w-8 h-8 animate-spin" style={{ color: GREEN }} /></div>
      ) : products.length === 0 ? (
        <div className="text-center text-gray-400 pt-10">Nothing here.</div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-black/5 divide-y divide-gray-50">
          {products.map((p) => {
            const name = p.normalized_name;
            const isOpen = expanded === name;
            const b = busy[name];
            return (
              <div key={p.id || name} className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate" style={{ color: TEXT }}>{p.name_original || name}</p>
                    <p className="text-xs text-gray-400 truncate">
                      {(p.source_contexts || []).join(", ") || "—"} · {p.hit_count || 1}×
                      {p.status === "added_to_db" && p.resolved_product_key ? ` · → ${p.resolved_product_key}` : ""}
                    </p>
                  </div>

                  {tab === "pending_review" && (
                    <>
                      <button onClick={() => enrich(name, p.user_suggested_category)} disabled={b}
                        className="px-3 py-1.5 rounded-lg text-sm font-medium text-white flex items-center gap-1.5 disabled:opacity-50" style={{ background: GREEN }}>
                        {b ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Enrich
                      </button>
                      <button onClick={() => reject(name)} disabled={b} className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg"><X className="w-4 h-4" /></button>
                    </>
                  )}

                  {tab === "needs_review" && (
                    <button onClick={() => (isOpen ? setExpanded(null) : openForm(name, p.enrich_draft))}
                      className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-200 flex items-center gap-1" style={{ color: TEXT }}>
                      <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} /> Review
                    </button>
                  )}

                  {tab === "added_to_db" && (
                    <span className="text-xs px-2 py-1 rounded-lg" style={{ background: "#E1F5EE", color: "#085041" }}>added</span>
                  )}
                </div>

                {isOpen && form && (
                  <ReviewForm form={form} upd={upd} busy={b}
                    onApprove={() => approve(name)} onReject={() => reject(name)} onReenrich={() => enrich(name)} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", list }) {
  return (
    <label className="block">
      <span className="text-xs text-gray-500">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} list={list}
        className="mt-0.5 w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:border-[#7B9E7F]" style={{ color: "#3D4035" }} />
    </label>
  );
}

function ReviewForm({ form, upd, busy, onApprove, onReject, onReenrich }) {
  const isNew = form.placement === "new";
  return (
    <div className="mt-3 border border-gray-200 rounded-xl p-4 bg-gray-50">
      <div className="rounded-lg p-3 mb-4" style={{ background: "#EAF1FB" }}>
        <p className="text-xs mb-2 flex items-center gap-1.5" style={{ color: "#185FA5" }}><Sparkles className="w-3.5 h-3.5" /> AI placed this here — verify before approving</p>
        <div className="flex gap-2 mb-3">
          <button onClick={() => upd("placement", "new")} className={`px-3 py-1.5 rounded-lg text-sm ${isNew ? "text-white" : "bg-white border border-gray-200 text-gray-500"}`} style={isNew ? { background: "#185FA5" } : {}}>New top-level</button>
          <button onClick={() => upd("placement", "subtype")} className={`px-3 py-1.5 rounded-lg text-sm ${!isNew ? "text-white" : "bg-white border border-gray-200 text-gray-500"}`} style={!isNew ? { background: "#185FA5" } : {}}>Subtype of existing</button>
        </div>
        {isNew ? (
          <div className="grid grid-cols-3 gap-3">
            <Field label="category" value={form.category} onChange={(v) => upd("category", v)} list="cat-hints" />
            <Field label="icon (emoji)" value={form.icon} onChange={(v) => upd("icon", v)} />
            <Field label="shelf_life (days)" type="number" value={form.shelf_life} onChange={(v) => upd("shelf_life", v)} />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <Field label="parent product key" value={form.parent_key} onChange={(v) => upd("parent_key", v)} />
            <Field label="category (inherited)" value={form.category} onChange={(v) => upd("category", v)} list="cat-hints" />
          </div>
        )}
      </div>
      <datalist id="cat-hints">{CATEGORY_HINTS.map((c) => <option key={c} value={c} />)}</datalist>

      <p className="text-xs font-medium text-gray-500 mb-2">Names</p>
      <div className="grid grid-cols-3 gap-3 mb-1">
        <Field label="ua" value={form.ua} onChange={(v) => upd("ua", v)} />
        <Field label="en" value={form.en} onChange={(v) => upd("en", v)} />
        <Field label="ru" value={form.ru} onChange={(v) => upd("ru", v)} />
      </div>
      {isNew && <div className="mb-4 mt-1"><Field label="internal key" value={form.product_key} onChange={(v) => upd("product_key", v)} /></div>}

      <p className="text-xs font-medium text-gray-500 mb-2 mt-3">Macros (per 100g)</p>
      <div className="grid grid-cols-4 gap-3 mb-4">
        <Field label="cal" type="number" value={form.cal} onChange={(v) => upd("cal", v)} />
        <Field label="p" type="number" value={form.p} onChange={(v) => upd("p", v)} />
        <Field label="f" type="number" value={form.f} onChange={(v) => upd("f", v)} />
        <Field label="c" type="number" value={form.c} onChange={(v) => upd("c", v)} />
      </div>

      <div className="mb-4"><Field label="vitamins (comma-separated)" value={form.vitamins} onChange={(v) => upd("vitamins", v)} /></div>

      <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
        <button onClick={onReject} disabled={busy} className="px-3 py-1.5 rounded-lg text-sm text-red-500 hover:bg-red-50">Reject</button>
        <button onClick={onReenrich} disabled={busy} className="px-3 py-1.5 rounded-lg text-sm border border-gray-200 flex items-center gap-1 text-gray-600"><RefreshCw className="w-3.5 h-3.5" /> Re-enrich</button>
        <button onClick={onApprove} disabled={busy} className="px-3 py-1.5 rounded-lg text-sm text-white flex items-center gap-1.5 disabled:opacity-50" style={{ background: "#1D9E75" }}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Add to DB
        </button>
      </div>
    </div>
  );
}
